import type { VariantProps } from 'class-variance-authority';
import { cx as clsx, cva } from 'class-variance-authority';
import type {
  ClassProp,
  ClassValue,
  StringToBoolean,
} from 'class-variance-authority/types';
import { twMerge } from 'tailwind-merge';

/**
 * clsx wrapper that automatically applies tailwind-merge to the output
 */
export const clstx: (...inputs: ClassValue[]) => string = (...inputs) => {
  return twMerge(clsx(inputs));
};

// So class-variance-authority doesn't export all their types 🥱
// Re-declaring the ones we need here.
// In `cva@beta`, these are exported properly, so we can remove this when they release v1.
type ConfigSchema = Record<string, Record<string, ClassValue>>;
type ConfigVariants<T extends ConfigSchema> = {
  [Variant in keyof T]?: StringToBoolean<keyof T[Variant]> | null | undefined;
};
type ConfigVariantsMulti<T extends ConfigSchema> = {
  [Variant in keyof T]?:
    | StringToBoolean<keyof T[Variant]>
    | StringToBoolean<keyof T[Variant]>[]
    | undefined;
};
type Config<T> = T extends ConfigSchema
  ? {
      variants?: T;
      defaultVariants?: ConfigVariants<T>;
      compoundVariants?: (T extends ConfigSchema
        ? (ConfigVariants<T> | ConfigVariantsMulti<T>) & ClassProp
        : ClassProp)[];
    }
  : never;
type Props<T> = T extends ConfigSchema
  ? ConfigVariants<T> & ClassProp
  : ClassProp;

/**
 * A wrapper around `cva` that automatically applies `tailwind-merge` to the output.
 *
 * @example
 * ```tsx
 * const buttonVariants = cvaMerge("font-semibold rounded", {
 *   variants: {
 *     intent: { primary: "bg-blue-500", secondary: "bg-gray-200" },
 *     size: { sm: "text-sm", md: "text-base" },
 *   },
 *   defaultVariants: { intent: "primary", size: "md" },
 * });
 *
 * type ButtonProps = VariantProps<typeof buttonVariants>;
 * // Inferred as: { intent?: "primary" | "secondary", size?: "sm" | "md" }
 * ```
 */
export function cvaMerge<T>(base?: ClassValue, config?: Config<T>) {
  const variantsFn = cva(base, config);

  return (props?: Props<T>) => twMerge(variantsFn(props));
}

// Re-export for convenience
export { clsx, cva };
export type { VariantProps };
